import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';

const EditorContainer = styled.div`
  display: flex;
  flex-direction: column;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
`;

const Toolbar = styled.div`
  display: flex;
  gap: 8px;
  padding: 12px;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  flex-wrap: wrap;
`;

const ToolbarButton = styled.button`
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  background: white;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f3f4f6;
    border-color: #9ca3af;
  }
  
  &:active {
    background: #e5e7eb;
  }
  
  &.active {
    background: #3b82f6;
    color: white;
    border-color: #3b82f6;
  }
`;

const EditorTextarea = styled.textarea`
  width: 100%;
  min-height: 300px;
  padding: 16px;
  border: none;
  outline: none;
  font-size: 14px;
  line-height: 1.6;
  font-family: inherit;
  resize: vertical;
  
  &:focus {
    outline: none;
  }
`;

const EditorDiv = styled.div`
  width: 100%;
  min-height: 300px;
  padding: 16px;
  border: none;
  outline: none;
  font-size: 14px;
  line-height: 1.6;
  font-family: inherit;
  overflow-y: auto;
  
  &:focus {
    outline: none;
    background: #fefefe;
  }
  
  &[contenteditable="true"] {
    cursor: text;
  }
  
  /* Placeholder styling for empty contentEditable */
  &:empty:before {
    content: attr(data-placeholder);
    color: #9ca3af;
    font-style: italic;
  }
  
  /* Style the HTML elements */
  strong, b {
    font-weight: 700;
  }
  
  em, i {
    font-style: italic;
  }
  
  u {
    text-decoration: underline;
  }
  
  h2 {
    font-size: 1.5em;
    font-weight: 600;
    margin: 12px 0;
  }
  
  h3 {
    font-size: 1.3em;
    font-weight: 600;
    margin: 10px 0;
  }
  
  ul {
    margin: 8px 0;
    padding-left: 24px;
  }
  
  ol {
    margin: 8px 0;
    padding-left: 24px;
  }
  
  li {
    margin: 4px 0;
  }
  
  p {
    margin: 8px 0;
  }
`;

const InlineImageUpload = styled.div`
  border: 2px dashed #d1d5db;
  border-radius: 8px;
  padding: 16px;
  margin: 8px 0;
  background: #f9fafb;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: #3b82f6;
    background: #f0f9ff;
  }
`;

const UploadArea = styled.div`
  border: 2px dashed #d1d5db;
  border-radius: 8px;
  padding: 40px 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 16px;
  
  &:hover {
    border-color: #3b82f6;
    background: #f8fafc;
  }
`;

const FileInput = styled.input`
  display: none;
`;

const ImagePreview = styled.img`
  max-width: 100%;
  max-height: 200px;
  border-radius: 8px;
  margin: 16px 0;
`;

const AltTextInput = styled.input`
  width: 100%;
  padding: 12px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  margin-bottom: 16px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const Button = styled.button`
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &.primary {
    background: #3b82f6;
    color: white;
    
    &:hover {
      background: #2563eb;
    }
  }
  
  &.secondary {
    background: #6b7280;
    color: white;
    
    &:hover {
      background: #4b5563;
    }
  }
`;

interface BlogContentEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

const BlogContentEditor: React.FC<BlogContentEditorProps> = ({
  value,
  onChange,
  placeholder = "Start writing your blog content..."
}) => {
  const [showImageUpload, setShowImageUpload] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [altText, setAltText] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const editorRef = useRef<HTMLDivElement>(null);

  // Set initial content only once when value changes from parent
  useEffect(() => {
    const editor = editorRef.current;
    if (editor && value !== editor.innerHTML) {
      editor.innerHTML = value || '';
    }
  }, [value]);

  const insertFormatting = (format: string) => {
    const editor = editorRef.current;
    if (!editor) return;

    editor.focus();
    
    // Use document.execCommand for proper formatting
    try {
      if (format === '**bold**') {
        document.execCommand('bold', false);
      } else if (format === '*italic*') {
        document.execCommand('italic', false);
      } else if (format === '- ') {
        document.execCommand('insertUnorderedList', false);
      }
      
      // Update the value with the current HTML content
      onChange(editor.innerHTML);
    } catch (error) {
      console.error('Formatting error:', error);
    }
  };

  const handleImageUpload = async () => {
    if (!selectedFile) return;

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('image', selectedFile);

      const response = await fetch(`${process.env.REACT_APP_BLOG_SERVICE_URL || 'http://localhost:3011'}/api/blogs/upload-inline-image`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error('Failed to upload image');
      }

      const data = await response.json();
      
      // Insert image placeholder in content
      const imagePlaceholder = `[IMAGE:${data.filename}]`;
      const textarea = document.querySelector('textarea') as HTMLTextAreaElement;
      const start = textarea?.selectionStart || value.length;
      const newContent = value.substring(0, start) + imagePlaceholder + value.substring(start);
      
      onChange(newContent);
      
      // Close upload form and reset
      setShowImageUpload(false);
      setSelectedFile(null);
      setImagePreview(null);
      setAltText('');
      
    } catch (error) {
      console.error('Image upload failed:', error);
      alert('Failed to upload image. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileSelect = (file: File) => {
    if (!file.type.startsWith('image/')) {
      alert('Please select a valid image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert('Image file must be less than 5MB');
      return;
    }

    setSelectedFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
      setShowImageUpload(true);
    }
  };

  const handleRightClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowImageUpload(true);
  };

  return (
    <EditorContainer>
      <Toolbar>
        <ToolbarButton onClick={() => insertFormatting('**bold**')}>
          <strong>B</strong>
        </ToolbarButton>
        <ToolbarButton onClick={() => insertFormatting('*italic*')}>
          <em>I</em>
        </ToolbarButton>
        <ToolbarButton onClick={() => setShowImageUpload(true)}>
          📷 Insert Image
        </ToolbarButton>
        <ToolbarButton onClick={() => insertFormatting('- ')}>
          📋 List
        </ToolbarButton>
      </Toolbar>
      
      <EditorDiv
        ref={editorRef}
        contentEditable={true}
        onInput={(e) => onChange(e.currentTarget.innerHTML)}
        onContextMenu={handleRightClick}
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
        data-placeholder={placeholder}
        suppressContentEditableWarning={true}
      />

      {showImageUpload && (
        <InlineImageUpload>
          <h4 style={{ margin: '0 0 12px 0', color: '#1f2937', fontSize: '16px' }}>
            📷 Insert Image
          </h4>
          
          <UploadArea onClick={() => fileInputRef.current?.click()}>
            <div style={{ color: '#6b7280', marginBottom: '8px' }}>
              📁 Click to browse or drag & drop image file
            </div>
            <div style={{ fontSize: '12px', color: '#9ca3af' }}>
              Supports: JPG, PNG, GIF • Max size: 5MB
            </div>
          </UploadArea>
          
          <FileInput
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleFileSelect(file);
            }}
          />
          
          {imagePreview && (
            <ImagePreview src={imagePreview} alt="Preview" />
          )}
          
          <AltTextInput
            type="text"
            placeholder="Alt text for image (optional)"
            value={altText}
            onChange={(e) => setAltText(e.target.value)}
          />
          
          <ButtonGroup style={{ marginTop: '12px' }}>
            <Button
              className="secondary"
              onClick={() => {
                setShowImageUpload(false);
                setSelectedFile(null);
                setImagePreview(null);
                setAltText('');
              }}
            >
              Cancel
            </Button>
            <Button
              className="primary"
              onClick={handleImageUpload}
              disabled={!selectedFile || isUploading}
            >
              {isUploading ? 'Uploading...' : 'Insert Image'}
            </Button>
          </ButtonGroup>
        </InlineImageUpload>
      )}
    </EditorContainer>
  );
};

export default BlogContentEditor;
